import {
  fetchAttendanceEventBriefing,
  fetchAttendanceEventLogoutSummary,
  fetchTeamAttendanceAnalytics,
} from '../services/attendanceService';
import { formatTaskStatusLabel } from '../components/attendance/attendanceTaskStatus';

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const mapSummaryTasks = (tasks = []) => tasks.map((task) => ({
  id: task.id,
  title: task.title || 'Task',
  status: task.status || 'COMPLETED',
  minutes: task.minutes ?? task.estimateTimeInMinutes ?? null,
}));

/** Leave label only — never include reason text. */
export const formatLeaveStatusLabel = (leaveLabelOrMessage) => {
  const raw = String(leaveLabelOrMessage || '').trim();
  if (!raw) return 'On leave';
  const withoutReason = raw.split('·')[0].trim();
  if (/full\s*day/i.test(withoutReason)) return 'Full day leave';
  if (/half\s*day/i.test(withoutReason)) return 'Half day leave';
  if (/leave$/i.test(withoutReason)) return withoutReason;
  if (/^leave$/i.test(withoutReason) || /^leave request$/i.test(withoutReason)) {
    return 'On leave';
  }
  return `${withoutReason} leave`;
};

const mergeLiveTasksIntoBriefing = (briefing, day) => {
  if (day.status !== 'CHECKED_IN') {
    return briefing;
  }
  const activeById = new Map((day.activeTasks || []).map((task) => [task.id, task]));
  const mergedTasks = (briefing.tasks || []).map((item) => {
    const live = activeById.get(item.id);
    if (!live) return item;
    return {
      ...item,
      title: live.title || item.title,
      subtitle: formatTaskStatusLabel(live.status),
      minutes: live.minutes ?? item.minutes,
    };
  });
  const existingIds = new Set(mergedTasks.map((task) => task.id));
  const extraActive = (day.activeTasks || [])
    .filter((task) => !existingIds.has(task.id))
    .map((task) => ({
      id: task.id,
      title: task.title || 'Task',
      subtitle: formatTaskStatusLabel(task.status),
      minutes: task.minutes ?? null,
    }));
  return {
    ...briefing,
    dayLabel: briefing.dayLabel || day.dayLabel,
    dateLabel: briefing.dateLabel || day.dateLabel,
    date: briefing.date || day.date,
    dayType: briefing.dayType || day.dayType,
    tasks: [...mergedTasks, ...extraActive],
  };
};

const buildDayStats = (day, summary) => ({
  logoutTime: summary?.logoutTime || day.checkOutTime || null,
  totalTimeSpent: summary?.totalTimeSpent || day.totalTimeSpent || '0m',
  totalBreakTime: summary?.totalBreakTime
    || day.totalBreakTime
    || (day.totalBreakMinutes ? `${day.totalBreakMinutes}m` : '0m'),
  checkInTime: summary?.checkInTime || day.checkInTime || '—',
  productivityPercent: summary?.productivityPercent ?? day.productivityPercent,
});

/**
 * Load a shared user's day briefing for manager view (current day by default).
 * Returns { type: 'LEAVE' } when the day is leave-only (no check-in session).
 */
export async function loadSharedUserDayStatus({ userId, date } = {}) {
  if (!userId) {
    const err = new Error('User is required');
    err.code = 'INVALID';
    throw err;
  }

  const targetDate = date || formatDateInput(new Date());
  // Analytics fills leave-only days (progress skips days with no check-in/out).
  const progress = await fetchTeamAttendanceAnalytics(userId, targetDate, targetDate);
  const days = progress?.days || [];
  const day = days.find((d) => d.date === targetDate) || days[0];

  if (!day?.checkInEventId) {
    if (day?.onLeave) {
      return {
        type: 'LEAVE',
        userName: progress?.userName || 'User',
        leaveLabel: formatLeaveStatusLabel(day.leaveLabel || 'Full day'),
        leaveStatus: day.leaveStatus || null,
        dateLabel: day.dateLabel || null,
        dayLabel: day.dayLabel || null,
        day,
      };
    }
    const err = new Error('No attendance session for this day');
    err.code = 'NO_SESSION';
    throw err;
  }

  const briefing = await fetchAttendanceEventBriefing(day.checkInEventId);
  let completedTasks = mapSummaryTasks(day.completedTasks);
  let summary = null;

  if (day.checkOutEventId) {
    summary = await fetchAttendanceEventLogoutSummary(day.checkOutEventId);
    if (completedTasks.length === 0) {
      completedTasks = mapSummaryTasks(summary.completedTasks);
    }
  }

  return {
    type: 'DAY',
    userName: progress?.userName || 'User',
    briefing: mergeLiveTasksIntoBriefing(briefing, day),
    completedTasks,
    comment: summary?.comment || '',
    completedMeta: summary ? {
      completedRemindersCount: summary.completedRemindersCount ?? 0,
      totalCompletedReminderMinutes: summary.totalCompletedReminderMinutes ?? 0,
    } : null,
    dayStats: buildDayStats(day, summary),
    headerSubtitle: day.status === 'LOGGED_OUT' ? 'Attendance record' : "Today's work in progress",
    day,
  };
}
